/**
 * ClientIdeasView Component
 *
 * Client portal: review pending ideas with a 1-5 star rating + optional
 * comment. Unrated ideas surface at the top; rated ones drop below.
 *
 * Backed by `PATCH /api/client-portal/ideas/:id/review`. Optimistic UI:
 * the local `reviews` map is the source of truth for what the user
 * sees, and writes go through the API in the background. Failed writes
 * roll back so the UI stays consistent with the server.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { SpinnerIcon, PlusIcon } from "@/components/icons";
import { Toast } from "@/components/ui/Toast";
import { IdeaCard } from "@/domains/Ideas/components/IdeaCard";
import type { Idea } from "@/domains/Ideas/types";
import {
  clientIdeasService,
  ClientIdea,
  CreateClientIdeaInput,
} from "../services/clientIdeasService";
import { ClientIdeaDetailDrawer } from "./ClientIdeaDetailDrawer";
import { CreateClientIdeaModal } from "./CreateClientIdeaModal";
import { markPortalApproval } from "../push/approvalSignal";

interface ClientIdeasViewProps {
  clientId: string;
  token: string;
}

interface LocalReview {
  rating: number | null;
  comment: string;
}

function reviewFromIdea(idea: ClientIdea): LocalReview {
  return {
    rating: typeof idea.clientRating === "number" ? idea.clientRating : null,
    comment: idea.clientComment ?? "",
  };
}

export const ClientIdeasView: React.FC<ClientIdeasViewProps> = ({
  clientId,
  token,
}) => {
  const [ideas, setIdeas] = useState<ClientIdea[]>([]);
  const [readyIdeas, setReadyIdeas] = useState<ClientIdea[]>([]);
  const [reviews, setReviews] = useState<Record<string, LocalReview>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeIdeaId, setActiveIdeaId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showSubmitToast, setShowSubmitToast] = useState(false);

  const fetchIdeas = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [pending, ready] = await Promise.all([
        clientIdeasService.getPendingIdeas(token),
        clientIdeasService.getReadyIdeas(token),
      ]);
      setIdeas(pending);
      setReadyIdeas(ready);
      // Seed the review map from server state so previously-rated ideas
      // show their stars on first paint (both queues).
      const seeded: Record<string, LocalReview> = {};
      for (const idea of [...pending, ...ready]) {
        const r = reviewFromIdea(idea);
        if (r.rating !== null || r.comment) seeded[idea.id] = r;
      }
      setReviews(seeded);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load ideas");
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchIdeas();
  }, [fetchIdeas]);

  // Optimistic apply: update local state immediately, then await the
  // API. On failure, roll back to the previous value. Avoids the lag
  // that would otherwise show between tap and card movement.
  const persistReview = useCallback(
    async (ideaId: string, next: LocalReview) => {
      const previous = reviews[ideaId];

      setReviews((prev) => {
        const map = { ...prev };
        if (next.rating === null && !next.comment) {
          delete map[ideaId];
        } else {
          map[ideaId] = next;
        }
        return map;
      });

      try {
        await clientIdeasService.reviewIdea(token, ideaId, {
          rating: next.rating,
          comment: next.comment.length > 0 ? next.comment : null,
        });
        if (next.rating !== null || next.comment) {
          // Value moment reached: allow the post-approval permission ask.
          markPortalApproval();
        }
      } catch (err) {
        // Roll back on failure so UI matches what the server has.
        console.error("review save failed", err);
        setReviews((prev) => {
          const map = { ...prev };
          if (previous) map[ideaId] = previous;
          else delete map[ideaId];
          return map;
        });
        setError("Salvataggio non riuscito. Riprova.");
      }
    },
    [token, reviews],
  );

  const handleRate = (ideaId: string, rating: number | null) => {
    const current = reviews[ideaId] ?? { rating: null, comment: "" };
    persistReview(ideaId, { ...current, rating });
  };

  const handleComment = (ideaId: string, comment: string) => {
    const current = reviews[ideaId] ?? { rating: null, comment: "" };
    if (current.comment === comment) return;
    persistReview(ideaId, { ...current, comment });
  };

  const handleOpen = (idea: ClientIdea) => {
    setActiveIdeaId(idea.id);
  };

  // Fire-and-forget: on success we close the modal and confirm with a
  // toast. The proposed idea lands as an agency DRAFT and is not added to
  // this review list. On failure the modal stays open with the draft
  // intact so the client can retry.
  const handleCreate = useCallback(
    async (input: CreateClientIdeaInput) => {
      setSubmitError(null);
      setIsSubmitting(true);
      try {
        await clientIdeasService.createIdea(token, input);
        setShowCreate(false);
        setShowSubmitToast(true);
      } catch (err) {
        console.error("idea create failed", err);
        setSubmitError("Invio non riuscito. Riprova.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [token],
  );

  const activeIdea = activeIdeaId
    ? [...ideas, ...readyIdeas].find((i) => i.id === activeIdeaId) ?? null
    : null;

  // Sort: unrated first (newest → oldest), then rated (highest → lowest,
  // tiebreak newest). Recomputed when reviews change so cards visibly
  // drop on rate. Comments don't affect sort — rating is the primary
  // signal.
  const sortedIdeas = useMemo(() => {
    const withMeta = ideas.map((idea) => {
      const review = reviews[idea.id] ?? { rating: null, comment: "" };
      return {
        idea,
        rating: review.rating,
        comment: review.comment,
        createdAt: new Date(idea.createdAt).getTime(),
      };
    });
    withMeta.sort((a, b) => {
      const aRated = a.rating !== null;
      const bRated = b.rating !== null;
      if (aRated !== bRated) return aRated ? 1 : -1;
      if (aRated && bRated) {
        if (a.rating !== b.rating) return (b.rating ?? 0) - (a.rating ?? 0);
      }
      return b.createdAt - a.createdAt;
    });
    return withMeta;
  }, [ideas, reviews]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SpinnerIcon className="w-8 h-8 text-blue-600" />
      </div>
    );
  }

  if (error && ideas.length === 0) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
          <button
            onClick={fetchIdeas}
            className="mt-2 text-sm text-red-600 hover:text-red-800"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto px-4 lg:px-6 py-4 space-y-4">
      {/* The page title + back button live in the shared portal header
          (registered via `getPageTitle()` in ApprovalView). All we own
          here is the helper sentence, the propose button and the card
          grid. */}
      <div className="flex flex-col gap-3 px-1 sm:flex-row sm:items-start sm:justify-between">
        <p className="text-sm text-gray-600">
          Vota ogni idea da 1 a 5 stelle in base a quanto ti convince. Le
          idee non ancora votate restano in cima.
        </p>
        <button
          onClick={() => setShowCreate(true)}
          className="shrink-0 self-end inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 sm:self-start"
        >
          <PlusIcon className="w-4 h-4" />
          Proponi un'idea
        </button>
      </div>

      {/* Inline error banner for save failures while the list still
          renders the previous state. Distinguishes from the fatal
          empty-state error above. */}
      {error && ideas.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-md px-3 py-2 text-sm text-rose-700 flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-xs font-medium text-rose-600 hover:text-rose-800"
          >
            Chiudi
          </button>
        </div>
      )}

      {sortedIdeas.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center">
          <p className="text-gray-500">
            Nessuna idea da rivedere. Hai un&apos;idea? Proponila.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedIdeas.map(({ idea, rating }) => (
            <IdeaCard
              key={idea.id}
              viewMode="client"
              // ClientIdea is a field-limited Pick of Idea; the client
              // branch of IdeaCard only reads id/title/body/contentType
              // /resources, so widening to Idea is safe at the call site.
              idea={idea as Idea}
              rating={rating}
              onRate={(value) => handleRate(idea.id, value)}
              onOpen={() => handleOpen(idea)}
            />
          ))}
        </div>
      )}

      {readyIdeas.length > 0 && (
        <div className="space-y-3 pt-2">
          <div className="px-1">
            <h2 className="text-sm font-semibold text-gray-900">In produzione</h2>
            <p className="text-xs text-gray-500">
              Le idee approvate su cui stiamo lavorando.
            </p>
          </div>
          {readyIdeas.map((idea) => (
            <IdeaCard
              key={idea.id}
              viewMode="client"
              idea={idea as Idea}
              rating={reviews[idea.id]?.rating ?? null}
              disabled
              onOpen={() => handleOpen(idea)}
            />
          ))}
        </div>
      )}

      <CreateClientIdeaModal
        isOpen={showCreate}
        onClose={() => {
          setShowCreate(false);
          setSubmitError(null);
        }}
        onSubmit={handleCreate}
        isSubmitting={isSubmitting}
        error={submitError}
      />

      {showSubmitToast && (
        <Toast
          message="Idea inviata. Grazie!"
          type="success"
          onClose={() => setShowSubmitToast(false)}
        />
      )}

      <ClientIdeaDetailDrawer
        idea={activeIdea}
        isOpen={activeIdeaId !== null}
        onClose={() => setActiveIdeaId(null)}
        rating={activeIdeaId ? reviews[activeIdeaId]?.rating ?? null : null}
        comment={activeIdeaId ? reviews[activeIdeaId]?.comment ?? "" : ""}
        onRate={(value) => {
          if (activeIdeaId) handleRate(activeIdeaId, value);
        }}
        onCommentChange={(value) => {
          if (activeIdeaId) handleComment(activeIdeaId, value);
        }}
      />
    </div>
  );
};
